import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Title,
  TextInput,
  Select,
  Group,
  SimpleGrid,
  Text,
  Loader,
  Center,
  Button,
  Stack,
  SegmentedControl,
  Badge,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import { itemsApi } from '../api/items';
import { categoriesApi } from '../api/categories';
import { buildCategorySelectData } from '../utils/categories';
import { ITEM_STATUS_LABELS, ITEM_STATUS_COLORS } from '../constants/status';
import { ItemCard } from '../components/ItemCard';
import type { Item, ItemStatus } from '../types';
import { pageContainer, listRow, listRowThumb, listRowThumbImg } from '../styles/theme.css';
import { getSemester, daysAgoLabel } from '../utils/semester';


function ListRow({ item, categoryLabel }: { item: Item; categoryLabel: string }) {
  const navigate = useNavigate();
  return (
    <div className={listRow} onClick={() => navigate(`/items/${item.id}`)}>
      <div className={listRowThumb}>
        {item.cover_image ? (
          <img src={item.cover_image} alt={item.title} className={listRowThumbImg} />
        ) : (
          '📦'
        )}
      </div>
      <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
        <Group gap="xs" wrap="nowrap">
          <Text size="sm" fw={600} lineClamp={1} style={{ flex: 1 }}>
            {item.title}
          </Text>
          <Badge size="xs" color={ITEM_STATUS_COLORS[item.status]} variant="light" tt="none">
            {ITEM_STATUS_LABELS[item.status]}
          </Badge>
        </Group>
        <Text size="xs" c="dimmed" lineClamp={1}>
          {[categoryLabel, item.color, item.brand].filter(Boolean).join(' · ')}
        </Text>
        <Group gap={6}>
          <Badge size="xs" variant="light" color="blue" tt="none">
            {getSemester(item.found_date)}
          </Badge>
          <Text size="xs" c="dimmed">
            {item.storage_location && `📍 ${item.storage_location} · `}
            {daysAgoLabel(item.found_date)}
          </Text>
        </Group>
      </Stack>
    </div>
  );
}

export function Dashboard() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<ItemStatus | 'all'>('available');
  const [category, setCategory] = useState<string | null>(null);
  const [semester, setSemester] = useState<string | null>(null);
  const [view, setView] = useState<'grid' | 'list'>('grid');

  const { data: catData } = useQuery({
    queryKey: ['categories'],
    queryFn: categoriesApi.get,
    staleTime: Infinity,
  });

  const categorySelectData = buildCategorySelectData(catData);

  // Fetch all items (no filters) just to compute available semesters
  const { data: allItems } = useQuery({
    queryKey: ['items', 'all-for-semesters'],
    queryFn: () => itemsApi.list(),
    staleTime: 60_000,
  });

  const availableSemesters = useMemo(() => {
    if (!allItems) return [];
    const seen = new Set<string>();
    for (const item of allItems) seen.add(getSemester(item.found_date));
    // Sort: E before F within same year, newest first
    return Array.from(seen).sort((a, b) => {
      const yearA = parseInt(a.slice(1));
      const yearB = parseInt(b.slice(1));
      if (yearB !== yearA) return yearB - yearA;
      // Same year: E (fall) comes after F (spring) chronologically, show E first
      return a[0] === 'E' ? -1 : 1;
    });
  }, [allItems]);

  const {
    data: items,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['items', { search, status, category }],
    queryFn: () =>
      itemsApi.list({
        search: search || undefined,
        status: status === 'all' ? undefined : status,
        category: category ?? undefined,
      }),
  });

  const displayed = useMemo(() => {
    if (!items) return [];
    if (!semester) return items;
    return items.filter((item) => getSemester(item.found_date) === semester);
  }, [items, semester]);

  return (
    <div className={pageContainer}>
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={2}>Fundne genstande</Title>
          <Group gap="xs">
            <Tooltip label="Kortvisning">
              <ActionIcon
                variant={view === 'grid' ? 'filled' : 'default'}
                onClick={() => setView('grid')}
                aria-label="Kortvisning"
              >
                ⊞
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Listevisning">
              <ActionIcon
                variant={view === 'list' ? 'filled' : 'default'}
                onClick={() => setView('list')}
                aria-label="Listevisning"
              >
                ≡
              </ActionIcon>
            </Tooltip>
            <Button onClick={() => navigate('/items/new')}>+ Ny genstand</Button>
          </Group>
        </Group>

        {/* Filters */}
        <Stack gap="xs">
          <TextInput
            placeholder="Søg efter titel, mærke, farve..."
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />

          <Group wrap="nowrap" gap="xs">
            <SegmentedControl
              value={status}
              onChange={(v) => setStatus(v as ItemStatus | 'all')}
              data={[
                { label: 'Opbevaret', value: 'available' },
                { label: 'Udleveret', value: 'returned' },
                { label: 'Kasseret', value: 'discarded' },
                { label: 'Alle', value: 'all' },
              ]}
              size="xs"
            />
          </Group>

          <Group gap="xs" grow>
            <Select
              placeholder="Alle kategorier"
              clearable
              value={category}
              onChange={setCategory}
              data={categorySelectData}
              searchable
            />
            <Select
              placeholder="Alle semestre"
              clearable
              value={semester}
              onChange={setSemester}
              data={availableSemesters.map((s) => ({ label: s, value: s }))}
            />
          </Group>
        </Stack>

        {/* Results */}
        {isLoading && (
          <Center py="xl">
            <Loader />
          </Center>
        )}

        {error && <Text c="red">Fejl ved indlæsning: {(error as Error).message}</Text>}

        {!isLoading && !error && displayed.length === 0 && (
          <Center py="xl">
            <Text c="dimmed">Ingen genstande fundet.</Text>
          </Center>
        )}

        {displayed.length > 0 && view === 'grid' && (
          <SimpleGrid cols={{ base: 1, xs: 2, sm: 3 }} spacing="md">
            {displayed.map((item) => (
              <ItemCard key={item.id} item={item} />
            ))}
          </SimpleGrid>
        )}

        {displayed.length > 0 && view === 'list' && (
          <Stack gap={0}>
            {displayed.map((item) => (
              <ListRow
                key={item.id}
                item={item}
                categoryLabel={catData?.categories.find((c) => c.id === item.category_id)?.label_da ?? item.category_id}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </div>
  );
}
